"use client";
// 🪪 Sign in with a passkey. The gateway issues the challenge; the browser
// produces an assertion with the device's private key; NextAuth's "passkey"
// Credentials provider hands that assertion to the gateway to verify and
// mints the same session a password sign-in would.
import { useState } from "react";
import { signIn } from "next-auth/react";
import { useRouter } from "next/navigation";
import { API_BASE } from "@/lib/mytradeos-api";

const b64uToBuf = (s: string) => Uint8Array.from(atob(s.replace(/-/g, "+").replace(/_/g, "/") + "=".repeat((4 - s.length % 4) % 4)), (c) => c.charCodeAt(0)).buffer;
const bufToB64u = (b: ArrayBuffer) => btoa(String.fromCharCode(...new Uint8Array(b))).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");

export default function PasskeyLogin() {
  const router = useRouter();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const supported = typeof window !== "undefined" && !!window.PublicKeyCredential;
  if (!supported) return null;

  const go = async () => {
    setBusy(true); setErr("");
    try {
      const r = await fetch(`${API_BASE()}/api/v5/auth/passkey/options`, { method: "POST",
        headers: { "Content-Type": "application/json" }, body: JSON.stringify({}) });
      if (!r.ok) throw new Error("Could not start passkey sign-in.");
      const opts = await r.json() as { challenge: string; allowCredentials?: { id: string; type: string }[] } & Record<string, unknown>;
      const cred = await navigator.credentials.get({ publicKey: {
        ...(opts as unknown as PublicKeyCredentialRequestOptions),
        challenge: b64uToBuf(opts.challenge),
        allowCredentials: (opts.allowCredentials ?? []).map((c) => ({ ...c, id: b64uToBuf(c.id), type: "public-key" as const })),
      } }) as PublicKeyCredential | null;
      if (!cred) throw new Error("No passkey was presented.");
      const a = cred.response as AuthenticatorAssertionResponse;
      const assertion = {
        id: cred.id, rawId: bufToB64u(cred.rawId), type: cred.type,
        response: {
          clientDataJSON: bufToB64u(a.clientDataJSON), authenticatorData: bufToB64u(a.authenticatorData),
          signature: bufToB64u(a.signature), userHandle: a.userHandle ? bufToB64u(a.userHandle) : null,
        },
        clientExtensionResults: cred.getClientExtensionResults(),
      };
      const out = await signIn("passkey", { assertion: JSON.stringify(assertion), redirect: false });
      if (out?.error) throw new Error("That passkey was not accepted.");
      router.push("/mytradeos");
      router.refresh();
    } catch (e) {
      const m = (e as Error).message || "";
      setErr(/NotAllowedError|cancel|abort/i.test(m) ? "Cancelled." : m || "Passkey sign-in failed.");
    }
    setBusy(false);
  };

  return (
    <div className="mt-3">
      <button type="button" onClick={go} disabled={busy}
        className="flex w-full items-center justify-center gap-2 rounded-lg border px-4 py-3 text-sm font-semibold hover:bg-accent disabled:opacity-50">
        🪪 {busy ? "Waiting for your device…" : "Sign in with a passkey"}
      </button>
      {err && <p className="mt-2 text-center text-xs text-red-600">{err}</p>}
    </div>
  );
}
